import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getPersonalInfoSummary,
  PersonalInfoSummary,
} from 'src/api/dataVault/personalInfoSummary';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface ScopeEntry {
  status: Status;
  data: PersonalInfoSummary | null;
  error: string | null;
  lastFetchedAt: number | null;
}

interface SliceState {
  byScope: Record<string, ScopeEntry>;
}

const STALE_MS = 60_000;

const initialEntry: ScopeEntry = {
  status: 'idle',
  data: null,
  error: null,
  lastFetchedAt: null,
};

const initialState: SliceState = { byScope: {} };

export const scopeKey = (memberId?: number | null): string =>
  memberId ? String(memberId) : 'self';

export const fetchPersonalInfo = createAsyncThunk<
  PersonalInfoSummary,
  { memberId?: number | null; forceRefresh?: boolean },
  { state: { dataVaultPersonalInfo: SliceState }; rejectValue: string }
>(
  'dataVaultPersonalInfo/fetch',
  async ({ memberId }, { rejectWithValue }) => {
    try {
      return await getPersonalInfoSummary(memberId);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load personal info summary'
      );
    }
  },
  {
    condition: ({ memberId, forceRefresh }, { getState }) => {
      if (forceRefresh) {
        return true;
      }
      const key = scopeKey(memberId);
      const entry = getState().dataVaultPersonalInfo.byScope[key];
      if (!entry) {
        return true;
      }
      if (entry.status === 'loading') {
        return false;
      }
      if (
        entry.status === 'succeeded' &&
        entry.lastFetchedAt &&
        Date.now() - entry.lastFetchedAt < STALE_MS
      ) {
        return false;
      }
      return true;
    },
  }
);

const slice = createSlice({
  name: 'dataVaultPersonalInfo',
  initialState,
  reducers: {
    invalidate(state, action: PayloadAction<string>) {
      delete state.byScope[action.payload];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPersonalInfo.pending, (state, action) => {
        const key = scopeKey(action.meta.arg.memberId);
        state.byScope[key] = { ...initialEntry, status: 'loading' };
      })
      .addCase(fetchPersonalInfo.fulfilled, (state, action) => {
        const key = scopeKey(action.meta.arg.memberId);
        state.byScope[key] = {
          status: 'succeeded',
          data: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchPersonalInfo.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        const key = scopeKey(action.meta.arg.memberId);
        state.byScope[key] = {
          ...initialEntry,
          status: 'failed',
          error:
            (action.payload as string | undefined) ??
            action.error.message ??
            'Failed',
        };
      });
  },
});

export const { invalidate } = slice.actions;

export const selectPersonalInfo =
  (key: string) =>
  (state: { dataVaultPersonalInfo: SliceState }): ScopeEntry =>
    state.dataVaultPersonalInfo.byScope[key] ?? initialEntry;

export default slice.reducer;
